import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sanitiseString } from '@quickstock/shared';

import { api } from '../lib/api';
import { ConflictError, mutate } from '../lib/mutate';
import { Field, inputClass, Modal } from './Modal';
import { StoredNameHint } from './StoredNameHint';
import { formatCurrency } from '../lib/format';

interface ProductDetail {
  productId: number;
  name: string;
  barcode: string;
  salePrice: string;
  reorderThreshold: number;
  overstockThreshold: number;
  stock: number;
  categoryId: number;
  vatRateId: number | null;
}

interface Reference {
  categories: { categoryId: number; name: string }[];
  vatRates: { vatRateId: number; percentage: string }[];
}

/**
 * Edits a product's name, price and stock thresholds.
 *
 * The current values are fetched fresh when the dialog opens and sent back as
 * `expected`. There is no row-version column on `productdetails`, so this
 * compare-and-set is the only thing standing between two managers editing at
 * once and one of them silently losing their change.
 *
 * The name is normalised as it is typed, using the same `sanitiseString` the
 * till writes names through — accents folded, punctuation regularised,
 * uppercased. Showing the result live rather than only after saving matters
 * because `ProductName` is `UNIQUE`: somebody typing a name that normalises onto
 * an existing product should see that before they submit, not as a rejection
 * afterwards.
 *
 * ── Mounted only while it is open ─────────────────────────────────────────
 *
 * The caller renders this only when a product is being edited, and that is load
 * bearing rather than tidiness. A rejected save leaves its message in state
 * here, above the `<Modal>` that unmounts on close, so a dialog left mounted
 * between opens greeted the next product with the previous one's rejection —
 * "String must contain at least 1 character(s)" over a form nobody had touched.
 * Unmounting means every open starts from nothing, and it goes on meaning that
 * when another piece of state is added later.
 */
export function ProductEditDialog({
  storeId,
  productId,
  onClose,
}: {
  storeId: string;
  productId: number;
  onClose: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [name, setName] = useState('');

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['product', storeId, productId],
    queryFn: () =>
      api.get<{ product: ProductDetail }>(`/stores/${storeId}/products/${productId}`),
    // Always refetch on open: a price from a minute ago is exactly the stale
    // baseline that produces a conflict.
    staleTime: 0,
    // The app refetches on window focus by default, which is right for a page of
    // figures and wrong for a form. Here it would swap the baseline out from
    // under a half-typed edit, which is the very thing `expected` exists to
    // catch — and the form is remounted on every fetch (below), so it would also
    // discard what had been typed.
    refetchOnWindowFocus: false,
  });

  const product = data?.product;

  /**
   * Correct the cached row in place instead of invalidating it.
   *
   * The two mutations that leave the dialog open each save a single field, and
   * an invalidation would refetch underneath a form that is still being typed
   * in. Neither needs the response to know the new value — it is what we just
   * sent — so writing it to the cache keeps `expected` in step for the next
   * save without disturbing the form.
   */
  function patchProduct(fields: Partial<ProductDetail>): void {
    queryClient.setQueryData<{ product: ProductDetail }>(
      ['product', storeId, productId],
      (cached) => (cached ? { product: { ...cached.product, ...fields } } : cached),
    );
  }

  // Seeded from the fetched row rather than held as `defaultValue`, because the
  // preview underneath has to react to every keystroke.
  //
  // Keyed on the stored name and not on the row, so that saving a department —
  // which rewrites the cached row while the dialog stays open — cannot reach in
  // and overwrite a name the user is halfway through typing.
  const storedName = product?.name;
  useEffect(() => {
    if (storedName !== undefined) setName(storedName);
  }, [storedName]);

  const { data: reference } = useQuery({
    queryKey: ['reference', storeId],
    queryFn: () => api.get<Reference>(`/stores/${storeId}/reference`),
    staleTime: 5 * 60_000,
  });

  /**
   * Department and VAT are a separate endpoint because they live on
   * `masterproduct` while price and thresholds live on `productdetails`. Saving
   * them as one request would mean two UPDATEs that could half-succeed; keeping
   * them separate means each is its own atomic change with its own audit row.
   */
  const saveClassification = useMutation({
    mutationFn: (values: { categoryId: number; vatRateId: number | null }) =>
      mutate.patch(`/stores/${storeId}/products/${productId}/classification`, {
        ...values,
        expected: { categoryId: product!.categoryId, vatRateId: product!.vatRateId },
      }),
    onSuccess: (_result, values) => {
      patchProduct(values);
      void queryClient.invalidateQueries({ queryKey: ['products', storeId] });
      setError(null);
    },
    onError: (caught: unknown) => {
      setConflict(caught instanceof ConflictError);
      setError(caught instanceof Error ? caught.message : 'Could not save.');
    },
  });

  const rename = useMutation({
    mutationFn: (next: string) =>
      mutate.patch(`/stores/${storeId}/products/${productId}/name`, {
        name: next,
        expected: { name: product!.name },
      }),
    onSuccess: (_result, next) => {
      patchProduct({ name: next });
      void queryClient.invalidateQueries({ queryKey: ['products', storeId] });
    },
    onError: (caught: unknown) => {
      setConflict(caught instanceof ConflictError);
      setError(caught instanceof Error ? caught.message : 'Could not rename the product.');
    },
  });

  const save = useMutation({
    mutationFn: (values: {
      salePrice: string;
      reorderThreshold: number;
      overstockThreshold: number;
    }) =>
      mutate.patch(`/stores/${storeId}/products/${productId}/pricing`, {
        ...values,
        expected: {
          salePrice: product!.salePrice,
          reorderThreshold: product!.reorderThreshold,
          overstockThreshold: product!.overstockThreshold,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products', storeId] });
      void queryClient.invalidateQueries({ queryKey: ['catalogue-summary', storeId] });
      // This one was missing, and it is why a saved price came back old: the
      // list refreshed but this dialog's own cached row did not, so re-opening
      // seeded the form from the price that had just been replaced. Invalidating
      // rather than patching because the server normalises what it stores
      // ("5.5" comes back as "5.50") and that is the string `expected` compares.
      void queryClient.invalidateQueries({ queryKey: ['product', storeId, productId] });
      onClose();
    },
    onError: (caught: unknown) => {
      setConflict(caught instanceof ConflictError);
      setError(caught instanceof Error ? caught.message : 'Could not save.');
    },
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setConflict(false);

    // Read before the first await: `currentTarget` is nulled once React has
    // finished with the event, and the rename below is asynchronous.
    const form = new FormData(event.currentTarget);

    // Two endpoints, in order, because the name lives on `masterproduct` and the
    // price on `productdetails`. The rename goes first so that a name clash
    // stops the whole submit — a price saved alongside a rejected rename would
    // leave the dialog showing a failure over a change that did happen.
    const nextName = sanitiseString(name);
    if (product && nextName !== product.name) {
      try {
        await rename.mutateAsync(nextName);
      } catch {
        return; // onError has already put the reason on screen.
      }
    }

    save.mutate({
      salePrice: String(form.get('salePrice')),
      reorderThreshold: Number(form.get('reorderThreshold')),
      overstockThreshold: Number(form.get('overstockThreshold')),
    });
  }

  return (
    <Modal open title="Edit product" onClose={onClose}>
      {/*
        `isFetching`, not just `isPending`. The fields below are uncontrolled and
        seeded through `defaultValue`, which React applies when an input mounts
        and never again — so whichever row is in hand at that moment is the row
        the form keeps.

        Re-opening the dialog is exactly when that bites. React Query holds the
        row from the previous open, serves it immediately (so `isPending` is
        false and `data` is the old row) and refetches behind it; the form would
        mount against the stale copy, latch it into the inputs, and ignore the
        fresh row when it landed a moment later. The edit appeared to have been
        lost, and only a second open — by which point the cache had caught up —
        showed the saved value.

        Waiting for the fetch to settle means the inputs mount once, against a
        row that has just come from the server. It costs a flash of "Loading…"
        on re-open, and it is the same wait the first open already has.
      */}
      {isPending || isFetching || !product ? (
        <p className="py-6 text-center text-xs text-ink-faint">Loading…</p>
      ) : (
        <form onSubmit={(event) => void handleSubmit(event)}>
          <Field label="Name" htmlFor="productName">
            <input
              id="productName"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className={inputClass}
              maxLength={200}
              autoComplete="off"
              required
            />
          </Field>

          <StoredNameHint
            name={name}
            before={<span className="qs-figure">{product.barcode}</span>}
          />

          <Field
            label="Sale price"
            htmlFor="salePrice"
            hint={`Currently ${formatCurrency(product.salePrice)}`}
          >
            <input
              id="salePrice"
              name="salePrice"
              defaultValue={product.salePrice}
              className={`${inputClass} qs-figure`}
              type="number"
              step="0.01"
              min="0.01"
              required
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Reorder at" htmlFor="reorderThreshold">
              <input
                id="reorderThreshold"
                name="reorderThreshold"
                defaultValue={product.reorderThreshold}
                className={`${inputClass} qs-figure`}
                type="number"
                min="1"
                required
              />
            </Field>
            <Field label="Overstock above" htmlFor="overstockThreshold">
              <input
                id="overstockThreshold"
                name="overstockThreshold"
                defaultValue={product.overstockThreshold}
                className={`${inputClass} qs-figure`}
                type="number"
                min="2"
                required
              />
            </Field>
          </div>

          <p className="mb-4 text-2xs text-ink-faint">
            Stock is {product.stock} and is changed at the till, not here.
          </p>

          <div className="mb-4 border-t border-rule pt-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Department" htmlFor="categoryId">
                <select
                  id="categoryId"
                  defaultValue={product.categoryId}
                  onChange={(event) =>
                    saveClassification.mutate({
                      categoryId: Number(event.target.value),
                      vatRateId: product.vatRateId,
                    })
                  }
                  className={inputClass}
                >
                  {reference?.categories.map((category) => (
                    <option key={category.categoryId} value={category.categoryId}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="VAT rate" htmlFor="vatRateId">
                <select
                  id="vatRateId"
                  defaultValue={product.vatRateId ?? ''}
                  onChange={(event) =>
                    saveClassification.mutate({
                      categoryId: product.categoryId,
                      // Empty means exempt, which is a real state rather than a
                      // missing answer — the column is nullable for this reason.
                      vatRateId: event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                  className={inputClass}
                >
                  <option value="">Exempt</option>
                  {reference?.vatRates.map((rate) => (
                    <option key={rate.vatRateId} value={rate.vatRateId}>
                      {rate.percentage}%
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {/* Only while it is happening. The steady-state note explaining that
                these save separately was noise on a dialog that is read far more
                often than it is puzzled over. */}
            {saveClassification.isPending && (
              <p className="text-2xs text-ink-faint">Saving…</p>
            )}
          </div>

          {error && (
            <div
              role="alert"
              className={
                conflict
                  ? 'mb-3 rounded border border-warn/30 bg-warn-soft px-2.5 py-2 text-xs text-warn'
                  : 'mb-3 text-xs text-down'
              }
            >
              {error}
              {conflict && (
                <button
                  type="button"
                  onClick={() => {
                    void queryClient.invalidateQueries({
                      queryKey: ['product', storeId, productId],
                    });
                    setError(null);
                    setConflict(false);
                  }}
                  className="ml-2 underline"
                >
                  Reload the current values
                </button>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-rule px-3 py-1.5 text-xs text-ink-muted hover:text-ink"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={save.isPending || rename.isPending}
              className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-ink-inverse hover:bg-accent-hover disabled:opacity-60"
            >
              {save.isPending || rename.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
